import React, { useState, useEffect } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { CalendarClock } from "lucide-react";

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];

// "YYYY-MM-DDTHH:mm" (local, no timezone offset) <-> {date: Date, time: "HH:mm"} — same
// string shape every datetime-local input in this app already used, so swapping the input
// for this component never touches how callers store/send the value.
function parseLocal(v) {
  if (!v) return { date: undefined, time: "09:00" };
  const [datePart, timePart] = v.split("T");
  const [y, m, d] = (datePart || "").split("-").map(Number);
  if (!y || !m || !d) return { date: undefined, time: "09:00" };
  return { date: new Date(y, m - 1, d), time: timePart ? timePart.slice(0, 5) : "09:00" };
}
function formatLocal(date, time) {
  if (!date) return "";
  const y = date.getFullYear(), m = String(date.getMonth() + 1).padStart(2, "0"), d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}T${time || "09:00"}`;
}
function displayLabel(v) {
  if (!v) return null;
  const [datePart, timePart] = v.split("T");
  const [y, m, d] = (datePart || "").split("-");
  if (!y) return null;
  return `${d} ${MONTHS_SHORT[Number(m) - 1] || ""} ${y}, ${timePart || "00:00"}`;
}

// Replaces the bare native `<input type="datetime-local">` app-wide — the native picker's
// own calendar+time popup has no visible confirm step (pick a date, it silently jumps to
// time, click away and you're never quite sure it "took"). This wraps the same Calendar
// primitive already used elsewhere in the app in a Popover with an explicit time field and
// an OK button, so committing a date+time is a single unambiguous action every time.
export default function DateTimePicker({ value, onChange, placeholder = "Pilih tanggal & jam", testId = "dtp", className = "" }) {
  const [open, setOpen] = useState(false);
  const [draftDate, setDraftDate] = useState(undefined);
  const [draftTime, setDraftTime] = useState("09:00");

  useEffect(() => {
    if (open) {
      const { date, time } = parseLocal(value);
      setDraftDate(date);
      setDraftTime(time);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const confirm = () => {
    if (!draftDate) return;
    onChange(formatLocal(draftDate, draftTime));
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`${testId}-trigger`}
          className={`w-full flex items-center gap-2 h-10 px-3 rounded-md border border-white/10 bg-[#18181B] text-sm text-left ${value ? "" : "text-zinc-500"} ${className}`}>
          <CalendarClock size={15} className="text-zinc-500 shrink-0" />
          <span className="truncate">{value ? displayLabel(value) : placeholder}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="bg-[#0f0f12] border-white/10 w-auto p-3">
        <Calendar mode="single" selected={draftDate} onSelect={setDraftDate} className="p-0" data-testid={`${testId}-calendar`} />
        <div className="flex items-center gap-2 mt-2 pt-3 border-t border-white/10">
          <label className="text-xs text-zinc-400 shrink-0">Jam</label>
          <input type="time" data-testid={`${testId}-time`} value={draftTime}
            onChange={(e) => setDraftTime(e.target.value)}
            className="flex-1 h-9 px-2 rounded-md bg-[#18181B] border border-white/10 text-sm" />
        </div>
        <div className="flex items-center gap-2 mt-3">
          <Button type="button" variant="outline" onClick={() => setOpen(false)} className="flex-1 h-9 bg-transparent border-white/10">Batal</Button>
          <Button type="button" data-testid={`${testId}-ok`} onClick={confirm} disabled={!draftDate} className="flex-1 h-9 bg-blue-600 hover:bg-blue-700 disabled:opacity-50">OK</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
